import WebApp from "@twa-dev/sdk";

/**
 * Carries an invite code from the link that opened the app to the moment the
 * user is signed in.
 *
 * Two ways in:
 *   - browser:  https://<host>/?invite=<code>
 *   - Telegram: a Mini App opened with start_param "inv_<code>" (a direct
 *     t.me/<bot>/<app>?startapp=… link). The usual t.me/<bot>?start=inv_<code>
 *     link is redeemed by the bot itself and never reaches here.
 *
 * The code is parked in localStorage because a Google or Apple sign-in may
 * leave the page and come back, which would lose a value held only in memory.
 * It is removed from the address bar straight away so that a shared screenshot
 * or a copied URL does not leak a still-valid link.
 */
const KEY = "bee_invite";
const TG_PREFIX = "inv_";

function clean(raw: string | null | undefined): string | null {
  let code = (raw || "").trim();
  if (code.startsWith(TG_PREFIX)) code = code.slice(TG_PREFIX.length);
  return /^[A-Za-z0-9_-]{4,64}$/.test(code) ? code : null;
}

function store(code: string | null) {
  try {
    if (code) localStorage.setItem(KEY, code);
    else localStorage.removeItem(KEY);
  } catch { /* storage blocked — the in-memory value still works this session */ }
}

/** Read an invite from the current URL / Telegram launch, park it, return it. */
export function captureInvite(): string | null {
  let code: string | null = null;
  try {
    const url = new URL(window.location.href);
    code = clean(url.searchParams.get("invite"));
    if (url.searchParams.has("invite")) {
      url.searchParams.delete("invite");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
  } catch { /* malformed URL — fall through */ }
  if (!code) {
    try { code = clean(WebApp.initDataUnsafe?.start_param); } catch { /* not in Telegram */ }
  }
  if (code) store(code);
  return code ?? pendingInvite();
}

export function pendingInvite(): string | null {
  try { return clean(localStorage.getItem(KEY)); } catch { return null; }
}

export function clearInvite() {
  store(null);
}

/** "API 409: {"detail":"INVITE_USED"}" → "INVITE_USED". */
export function inviteErrorCode(e: unknown): string | null {
  const m = e instanceof Error ? e.message.match(/INVITE_[A-Z_]+/) : null;
  return m ? m[0] : null;
}

